import { useCallback, useEffect, useRef, useState } from "react";
import { FULLSCREEN_PANEL_SLIDE_MS } from "@constants/modalAnimation";

export const FULLSCREEN_PANEL_SLIDE_CLASSES = {
  IN: "container-slide-in",
  STATIC: "container-slide-static",
  OUT: "container-slide-out",
};

export const shouldSkipInitialSlideIn = (locationState) => {
  if (locationState?.skipSlideIn || locationState?.isReturning) {
    return true;
  }
  const navigationEntry = window.performance.getEntriesByType("navigation")[0];
  return navigationEntry?.type === "back_forward";
};

/** 滑出動畫結束後執行 callback（預約核銷等非 React hook 場景） */
export function runAfterFullscreenPanelSlide(callback) {
  return window.setTimeout(callback, FULLSCREEN_PANEL_SLIDE_MS);
}

/**
 * 全屏面板由右滑入／滑出狀態機（對齊 _fullscreen-panel-slide-animation.scss）
 *
 * @param {object} options
 * @param {boolean} [options.skipInitialSlideIn=false] 返回時直接呈現終態
 * @param {false | 'effect' | 'raf'} [options.deferEnter=false] 延遲觸發進場（退款、業績分潤 portal）
 */
export default function useFullscreenPanelSlide({
  skipInitialSlideIn = false,
  deferEnter = false,
} = {}) {
  const skipInitialSlideInRef = useRef(skipInitialSlideIn);
  const closeTimerRef = useRef(null);
  const [isExiting, setIsExiting] = useState(false);
  const [hasEntered, setHasEntered] = useState(() => deferEnter === false);

  useEffect(() => {
    if (deferEnter === "effect") {
      setHasEntered(true);
    }
  }, [deferEnter]);

  useEffect(() => {
    if (deferEnter !== "raf") return undefined;
    const frameId = window.requestAnimationFrame(() => {
      setHasEntered(true);
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [deferEnter]);

  useEffect(
    () => () => {
      if (closeTimerRef.current != null) {
        window.clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    },
    []
  );

  const slideClassName = isExiting
    ? FULLSCREEN_PANEL_SLIDE_CLASSES.OUT
    : !hasEntered
      ? ""
      : skipInitialSlideInRef.current
        ? FULLSCREEN_PANEL_SLIDE_CLASSES.STATIC
        : FULLSCREEN_PANEL_SLIDE_CLASSES.IN;

  const requestClose = useCallback(
    (onAfterClose, durationMs = FULLSCREEN_PANEL_SLIDE_MS) => {
      if (isExiting) return;
      setIsExiting(true);
      closeTimerRef.current = window.setTimeout(() => {
        closeTimerRef.current = null;
        setIsExiting(false);
        if (typeof onAfterClose === "function") {
          onAfterClose();
        }
      }, durationMs);
    },
    [isExiting]
  );

  return {
    slideClassName,
    requestClose,
    isExiting,
    hasEntered,
  };
}
